import { Auth } from 'aws-amplify';

import { StreamServiceAPIName } from 'src/constants';
import { buildApiEndpointUrl } from 'src/utils/AmplifyApiUtils';

import type {
  BuilderHistoryArg,
  BuilderHistoryPage,
} from './builderSessionApi';

export interface StreamServiceError {
  status: number | 'FETCH_ERROR' | 'PARSING_ERROR';
  data?: unknown;
}

export const HISTORY_UNAVAILABLE_ERROR =
  'History pagination is not available yet';

// The stream service authorizes purely on a Cognito ACCESS token (token_use=
// access + the username claim, which only access tokens carry), and its CORS
// allow-list is exactly Authorization/Accept/Last-Event-ID/Content-Type. The
// shared amplifyBaseQuery would attach the ID token plus Portal/Company headers,
// which trip the cross-origin preflight — so this is a direct fetch, with the
// portal id as a query param.
export const fetchBuilderHistoryPage = async (
  { appBuilderId, sessionId, portalId }: BuilderHistoryArg,
  cursor: string,
  signal?: AbortSignal,
): Promise<{ data: BuilderHistoryPage } | { error: StreamServiceError }> => {
  const params = new URLSearchParams({ historyPage: cursor });
  if (portalId) params.set('portalId', portalId);
  try {
    const accessToken = (await Auth.currentSession())
      .getAccessToken()
      .getJwtToken();
    const response = await fetch(
      buildApiEndpointUrl(
        StreamServiceAPIName,
        `/app-builders/${appBuilderId}/sessions/${sessionId}/messages/stream?${params.toString()}`,
      ),
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
        signal,
      },
    );
    if (!response.ok) {
      return { error: { status: response.status } };
    }
    // An older stream-service instance ignores historyPage and answers with an
    // SSE stream; refuse to parse it as a page.
    if (!response.headers.get('content-type')?.includes('application/json')) {
      void response.body?.cancel();
      return {
        error: { status: 'PARSING_ERROR', data: HISTORY_UNAVAILABLE_ERROR },
      };
    }
    const page = (await response.json()) as {
      messages: BuilderHistoryPage['messages'] | null;
      nextToken: string;
    };
    return {
      data: { messages: page.messages ?? [], nextToken: page.nextToken ?? '' },
    };
  } catch (err) {
    return { error: { status: 'FETCH_ERROR', data: String(err) } };
  }
};
